import api from './api';

/**
 * Canonical OOG (out-of-gauge) size/weight compute client.
 *
 * Calls POST /oog/compute — the backend's app/core/oog_specs.py, the SAME logic
 * the saverule `oog_checkspecs` runs server-side. This replaces the frontend's
 * local TypeScript calc (utils/overweightCheck.ts), so the order form and the
 * saverule share ONE implementation and can't silently drift (the reason the
 * threshold table used to be maintained in both Python and TS). Mirrors the
 * chassis-compatibility pattern, but as a compute call rather than a data fetch
 * because the OOG logic (leg-by-leg location/exception handling) is too heavy to
 * safely re-implement client-side.
 */

/** One leg's inputs. camelCase in; mapped to the backend snake_case payload. */
export interface OogComputeLeg {
  legNum: number;
  origCode?: number | string | null;
  destCode?: number | string | null;
  legAction2?: string | null;
  /** Leg destination arrival (v1 `arrivaldatetime`) — drives the delivered check. */
  arrivalDateTime?: string | null;
  /** True when the arrival was NOT edited this session (v1's new==old test). */
  arrivalUnchanged?: boolean;
}

export interface OogComputeInput {
  size?: string | null;
  equipType?: string | null;
  chassisType?: string | null;
  otr?: string | null;
  lbs?: number | string | null;
  lengthInches?: number | string | null;
  widthInches?: number | string | null;
  heightInches?: number | string | null;
  moveType?: string | null;
  billTo?: number | string | null;
  customer?: number | string | null;
  dispatcher?: string | null;
  isDaycab?: string | null;
  ooglegal?: string | null;
  originateLeg?: number;
  deliveryLeg?: number;
  terminateLeg?: number;
  legs: readonly OogComputeLeg[];
}

export interface OogComputeResult {
  /** Overweight respecting leg progress — drives the save flag + system approval. */
  overweight: boolean;
  /** Overweight ignoring leg progress — drives the persistent red weight highlight. */
  overweightForHighlight: boolean;
  /** Pounds over the matched threshold (0 when not overweight). */
  overLbs: number;
  /** Overweight but within every leg's customer lane exception -> auto-approve. */
  exceptionApproved: boolean;
  /** Short, user-facing explanation of the verdict (for the transition toast). */
  reason: string;
  /** Forced triaxle Y/N off the chassis type; null = leave unchanged. */
  triaxle: string | null;
  oversize: boolean;
  oog: boolean;
  /** Per-dimension over-amounts (inches), 0 when not oversize on that axis. */
  lengthOver: number;
  widthOver: number;
  heightOver: number;
  brokerout: boolean;
  /** 'Y'/'N' permit-required flags the UI would set from the verdict. */
  oversizepermitrequired: string;
  overweightpermitrequired: string;
}

const str = (v: unknown): string => (v == null ? '' : String(v));

export async function computeOog(input: OogComputeInput): Promise<OogComputeResult> {
  const res = await api.post('/oog/compute', {
    order: {
      size: str(input.size),
      equiptype: str(input.equipType),
      chassistype: str(input.chassisType),
      otr: str(input.otr),
      lbs: str(input.lbs),
      lengthinches: str(input.lengthInches),
      widthinches: str(input.widthInches),
      heightinches: str(input.heightInches),
      movetype: str(input.moveType),
      billto: str(input.billTo),
      customercode: str(input.customer),
      dispatcher: str(input.dispatcher),
      isdaycab: str(input.isDaycab),
      ooglegal: str(input.ooglegal),
    },
    legs: input.legs.map((l) => ({
      legnum: l.legNum,
      origcode: str(l.origCode),
      destcode: str(l.destCode),
      legaction2: str(l.legAction2),
      arrivaldatetime: str(l.arrivalDateTime),
      arrivalUnchanged: l.arrivalUnchanged ?? false,
    })),
    originateLeg: input.originateLeg ?? 0,
    deliveryLeg: input.deliveryLeg ?? 0,
    terminateLeg: input.terminateLeg ?? 0,
  });
  const d = res.data ?? {};
  return {
    overweight: !!d.overweight,
    overweightForHighlight: !!d.overweightForHighlight,
    overLbs: Number(d.lbsOver ?? 0),
    exceptionApproved: !!d.exceptionApproved,
    reason: str(d.reason),
    triaxle: d.triaxle ?? null,
    oversize: !!d.oversize,
    oog: !!d.oog,
    lengthOver: Number(d.lengthOver ?? 0),
    widthOver: Number(d.widthOver ?? 0),
    heightOver: Number(d.heightOver ?? 0),
    brokerout: !!d.brokerout,
    oversizepermitrequired: str(d.oversizepermitrequired),
    overweightpermitrequired: str(d.overweightpermitrequired),
  };
}
